import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import GlobalNavigation, {
    LinkMenuObject,
    LinkSection,
    LinkMenuObjectWithOptionalLabel,
    LinkSectionWithPendoId
} from '@velocityehs/uds.core.global-navigation';
import React, { useEffect } from 'react';
import axios from "axios";
import { changeLanguage } from "./translation/i18n";
import { initialData } from "./DefaultSettings";
import { useTranslation } from "react-i18next";

enum MenuType{
    Resources = "Resources",
    Analysis = "Analysis",
    Administration= "Administration"
}
declare global{
    interface Window{
        username: string,
        externalHeader: boolean,
        authenticated: boolean,
        logoutUrl: string,
        applicationLocale: string
    }
}

type productData = {
    entityID: string,
    id?: string,
    ribbon: string,
    relayStateUrl?: string
}

const host = initialData.host;

const getCookie = (name: string) => {
    function escape(s: string) { return s.replace(/([.*+?^${}()|[\]/\\])/g, '\\$1'); };
    const match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? match[1] : null;
}

function App() {
    const { t } = useTranslation();
    const [navigations, setNavigations] = React.useState([]);
    const [username, setUsername] = React.useState("");
    const [error, setError] = React.useState(true);
    const [enterpriseMenu, setEnterpriseMenu] = React.useState<LinkSectionWithPendoId>();
    const [jwt, setJWT] = React.useState<string>();
    const [velocityUserId, setVelocityUserId] = React.useState<string>();
    const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);
    const [showArchiveReport, setShowArchiveReport] = React.useState(false);
    const [isAdmin, setIsAdmin] = React.useState(false);
    const [logoutUrl, setLogoutUrl] = React.useState("");

    const decodeHtml = (inputStr?: string) => {
        if (inputStr == null) {
            // Prevent a null value from being parsed into "null"
            return "";
        }
        let doc = new DOMParser().parseFromString(inputStr, "text/html");
        return doc.documentElement.textContent || "";
    }

    const getNavigations= () => {
        axios.get('api/header')
            .then((response) => {
                if(response){
                    if(response.data.authenticated){
                        setUsername(decodeHtml(response.data.fullName));
                        setLogoutUrl(response.data.ssoLogoutUrl);
                        setNavigations(response.data.navigationList);
                        setIsSuperAdmin(response.data.superAdmin);
                        setIsAdmin(response.data.admin);
                        setShowArchiveReport(response.data.archiveDoc != null && response.data.showArchiveMenu);
                        setError(false);
                    }
                }
            })
    };

    useEffect(()=>{
        if(!window.externalHeader){
            getNavigations();
            getAccelerateSessionAttributes();
            setUsername(window.username);
            changeLanguage(window.applicationLocale);
        } else {
            const localeFromCookie = getCookie('com_humantech_app_locale');
            const applicationLocale = localeFromCookie ? localeFromCookie : 'en';
            changeLanguage(applicationLocale);
        }
    }, []);

    React.useEffect(() => {
        if(jwt && velocityUserId) {
            getEnterpriseMenu();
        }
    },[jwt, velocityUserId])

    const getAccelerateSessionAttributes = () => {
        axios.get('api/accelerate/sessionAttributes')
            .then((response:any) => {
                setJWT(response?.data?.jwt);
                setVelocityUserId(response?.data?.velocityUserId);
            })
    }

    const getEnterpriseMenu = () => {
        axios({
            method: 'post',
            headers: {
                Authorization: "Bearer " + jwt
            },
            url: process.env.REACT_APP_GRAPHQL_URL + '?query=assignedApplications',
            data: {
                query: "query getUserAssignedApplications($userId: ID) { user(id: $userId) { assignedApplications { id, applicationName, applicationCode, redirectUrl } } }",
                variables: {userId: velocityUserId}
            }
        })
            .then((response: any) => {
                setEnterpriseMenu({
                    sectionLinks: response?.data?.data.user.assignedApplications.map((product: any) => {
                        return {
                            text: product.applicationName,
                            url: product.redirectUrl,
                            sameTab: true,
                        }
                    })
                })
            })
            .catch(error => console.log(error))
    }

    const getNavigationHierarchy = (navigationListData: any) => {

        let returnArray:LinkMenuObject[] = [];
        if (!navigationListData) {
            console.log('Failed to build navigation hierarchy menu');
            return returnArray;
        }

        navigationListData.forEach((item: any)=>{
            const menu: LinkMenuObject ={
                dropDownLabel: t(item.label),
                pendoId: item.linkID
            };
            const menuSections = [];
            switch (item.linkID){
                case MenuType.Resources:
                    returnArray.push(
                        {
                            dropDownLabel: t("menu.resources"),
                            dropDownLink: `${host}/resources/list`,
                            sameTab: true
                        }
                    );
                    break;
                case MenuType.Analysis:
                    menuSections.push({
                            sectionTitle: t("common.Overview"),
                            sectionLinks: [
                                { text: t("common.Dashboard"), url: `${host}/manager/dashboard`, sameTab: true },
                                { text: t("report.training.page.title"), url: `${host}/manager/trainingReport`, sameTab: true },
                                { text: t("surveySorter.page.title"), url: `${host}/manager/records`, sameTab: true },
                                { text: t("manager.actionOverview.page.title"), url: `${host}/manager/actionOverview`, sameTab: true },
                            ]
                        },
                        {
                            sectionTitle: t("report.page.title"),
                            sectionLinks: [
                                { text: t("report.sectionConcernOverview.page.title"), url: `${host}/manager/records/sectionConcern`, sameTab: true },
                                { text: t("report.scoreOverview.page.title"), url: `${host}/manager/scoreOverviewReport`, sameTab: true },
                                { text: t("report.riskImprovementOverview.page.title"), url: `${host}/manager/riskImprovementOverviewReport`, sameTab: true },
                                { text: t("report.issue.page.title"), url: `${host}/manager/issueReport`, sameTab: true },
                                { text: t("report.discomfort.page.title"), url: `${host}/manager/discomfortReport`, sameTab: true },
                                (showArchiveReport) ? { text: t("report.archive.page.title"), url: `${host}/manager/archiveReport`, sameTab: true }:null,
                            ]
                        });
                    returnArray.push({
                        dropDownLabel: t("menu.analysis"),
                        dropDownSections: menuSections as LinkSection[]
                    });
                    break;
                case MenuType.Administration:
                    if(isSuperAdmin){
                        menuSections.push(
                            {
                                sectionTitle: t("admin.home.organization.title"),
                                sectionLinks: [
                                    { text: t("ou"), url: `${host}/ou/home`, sameTab: true },
                                    { text: t("admin.text.adminuser"), url: `${host}/userv2/list`, sameTab: true },
                                    { text: t("admin.text.mail"), url: `${host}/mail/compose`, sameTab: true },
                                ]
                            },
                            {
                                sectionTitle: t("admin.home.dataManagement.title"),
                                sectionLinks: [
                                    { text: t("usageMeter.page.accountUsage"), url: `${host}/usageMeter`, sameTab: true }
                                ]
                            }
                        );

                        returnArray.push({
                            dropDownLabel: t("admin.page.title"),
                            dropDownSections: menuSections as LinkSection[]
                        })
                    }else{
                        menuSections.push(
                            {
                                sectionTitle: t("admin.home.organization.title"),
                                sectionLinks: [
                                    { text: t("ou"), url: `${host}/ou/home`, sameTab: true },
                                    { text: t("admin.text.adminuser"), url: `${host}/userv2/list`, sameTab: true },
                                    { text: t("admin.text.mail"), url: `${host}/mail/compose`, sameTab: true },
                                ]
                            }
                        )

                        returnArray.push({
                            dropDownLabel: t("admin.page.title"),
                            dropDownSections: menuSections as LinkSection[]
                        });
                    }
            }
        });

        return returnArray;
    }
    const getUserMenu = (username: string) => {
        return{
            username: username,
            pendoId: "usermenu",
            sectionLinks:[
                {
                    text: t("yourAccount.page.title"),
                    url: `${host}/yourAccount/view`,
                    pendoId: "My Account",
                    sameTab: true
                },
                {
                    text:t("menu.resources"),
                    url: `${host}/resources/list`,
                    pendoId: "resources",
                    sameTab: true
                },
                {
                    text: t("menu.logout"),
                    url: logoutUrl == "logout"? `${host}/logout`: logoutUrl,
                    pendoId: "signout",
                    sameTab: true
                }

            ]
        }
    };
   const getSettingMenu = (): LinkMenuObjectWithOptionalLabel => {
                        let menuSections: LinkSection[] = [];
                        if (isSuperAdmin) {
                            menuSections.push(
                                {
                                    sectionTitle: t("admin.home.pagePreference.title"),
                                    sectionLinks: [
                                        { text: t("admin.text.adminCompanyLogoContact"), url: `${host}/pageGeneral`, sameTab: true },
                                        { text: t("pageConfig.page.title"), url: `${host}/pageConfig/list`, sameTab: true },
                                        { text: t("menu.resources"), url: `${host}/resources/list`, sameTab: true },
                                    ]
                                },
                                {
                                    sectionTitle: t("admin.home.officeAssessment.title"),
                                    sectionLinks: [
                                        { text: t("assessmentEditor.page.title"), url: `${host}/manager/surveyEditor/view?surveyID=11`, sameTab: true },
                                        { text: t("solutionEditor.page.title"), url: `${host}/manager/solutionEditor/surveyView?surveyID=11`, sameTab: true },
                                        { text: t("assessmentScoreSensitivity.page.title"), url: `${host}/manager/scoreRankOption`, sameTab: true }
                                    ]
                                },
                                {
                                    sectionTitle: t("admin.home.dataManagement.title"),
                                    sectionLinks: [
                                        { text: t("dataPurge.page.title"), url: `${host}/purgeSetting`, sameTab: true }
                                    ]
                                }
                            );
                        } else if (isAdmin) {
                            menuSections.push(
                                {
                                    sectionTitle: t("admin.home.pagePreference.title"),
                                    sectionLinks: [
                                        { text: t("pageConfig.page.title"), url: `${host}/pageConfig/list`, sameTab: true }
                                    ]
                                }
                            );
                        }
                        return {
                            dropDownSections: menuSections
                        } as LinkMenuObjectWithOptionalLabel ;
                    };
  return (
      <div className="App">
          <GlobalNavigation
              noRouter={true}
              productName={t("app.name")}
              homePageUrl={`${host}/home`}
              homePageSameTab={true}
              userMenu={!error ? getUserMenu(username) : undefined}
              enterpriseMenu={enterpriseMenu ?? undefined}
              navigationHierarchy={!error ?  getNavigationHierarchy(navigations): undefined}
              settingsMenu={!error && (isSuperAdmin|| isAdmin)?  getSettingMenu(): undefined}
              mainContentId="mainContent"
          />
      </div>
  );
}

export default App;
